import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import Assignation from "../models/Assignation";
import AssignationPause from "../models/AssignationPause";
import AssignationPauseService from "../services/AssignationPauseService";
import { QueryKeyFactory } from "../services/queryKeys";
import { unwrap } from "../utils/queryFn";

export const assignationPauseKeys = new QueryKeyFactory(AssignationPauseService.path);

export const useAssignationPauseDialog = (open: boolean, assignation?: Assignation) => {
  const queryClient = useQueryClient();
  const assignationExternalId = assignation?.externalId;
  const [shortStartDate, setShortStartDate] = useState<Date | null>(new Date());
  const [shortEndDate, setShortEndDate] = useState<Date | null>(new Date());
  const [reason, setReason] = useState<string>("");

  const pausesQuery = useQuery({
    queryKey: assignationPauseKeys.list({ assignationExternalId: assignationExternalId ?? "" }),
    queryFn: () => unwrap(AssignationPauseService.findByAssignation(assignationExternalId!)),
    enabled: open && !!assignationExternalId,
  });

  const refreshPauses = () =>
    queryClient.invalidateQueries({ queryKey: assignationPauseKeys.list({ assignationExternalId: assignationExternalId ?? "" }) });

  const addMutation = useMutation({
    mutationFn: (payload: AssignationPause) => unwrap(AssignationPauseService.createPause(payload)),
    onSuccess: async () => {
      setReason("");
      await refreshPauses();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (externalId: string) => unwrap(AssignationPauseService.deletePause(externalId)),
    onSuccess: () => refreshPauses(),
  });

  const handleAddPause = () => {
    if (!assignationExternalId) return;
    if (!shortStartDate) {
      NotificationService.error("Start date is required");
      return;
    }

    const payload = {
      assignationExternalId,
      shortStartDate,
      shortEndDate: shortEndDate ?? shortStartDate,
      reason: reason || undefined,
    } as AssignationPause;

    addMutation.mutate(payload);
  };

  const handleDeletePause = (externalId?: string) => {
    if (!externalId) return;
    deleteMutation.mutate(externalId);
  };

  const isLoading = pausesQuery.isFetching || addMutation.isPending || deleteMutation.isPending;

  return {
    shortStartDate,
    setShortStartDate,
    shortEndDate,
    setShortEndDate,
    reason,
    setReason,
    pauses: pausesQuery.data ?? [],
    isLoading,
    handleAddPause,
    handleDeletePause,
  };
};
